// src/components/Teacher/CreateAssignmentModal.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db, storage } from '../../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Notification from '../Account/Notification';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    surfaceLowest:    '#ffffff',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
    error:            '#b41340',
};
 
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    border: `1.5px solid ${C.outlineVariant}`,
    borderRadius: '10px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
    background: C.surfaceLowest,
    color: C.onSurface,
};
 
const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.8rem',
    fontWeight: '700',
    color: C.onSurfaceVariant,
    fontFamily: "'Epilogue', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};
 
interface ClassOption {
    id: string;
    name: string;
}
 
interface CreateAssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    classId: string;           // pass "" to show class selector
    className: string;
    onAssignmentCreated?: () => void;
}
 
const CreateAssignmentModal: React.FC<CreateAssignmentModalProps> = ({
    isOpen,
    onClose,
    classId: propClassId,
    className: propClassName,
    onAssignmentCreated,
}) => {
    const { currentUser } = useAuth();
 
    // Form state
    const [title, setTitle]               = useState('');
    const [description, setDescription]   = useState('');
    const [prompt, setPrompt]             = useState('');
    const [instructions, setInstructions] = useState('');
    const [minWords, setMinWords]         = useState<number | ''>('');
    const [maxWords, setMaxWords]         = useState<number | ''>('');
    const [dueDate, setDueDate]           = useState('');
    const [dueTime, setDueTime]           = useState('23:59');
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
    const [saving, setSaving]             = useState(false);
    const [error, setError]               = useState('');
 
    // Class selector state (only used when propClassId is empty)
    const [classes, setClasses]           = useState<ClassOption[]>([]);
    const [selectedClassId, setSelectedClassId]     = useState('');
    const [selectedClassName, setSelectedClassName] = useState('');
    const [loadingClasses, setLoadingClasses]       = useState(false);
 
    const needsClassSelector = !propClassId;
 
    // Notification state
    const [notification, setNotification] = useState<{
        show: boolean;
        type: 'assignment-created' | 'error';
        assignmentTitle?: string;
        errorMessage?: string;
        customMessage?: string;
        className?: string;
    }>({ show: false, type: 'assignment-created' });
 
    // Load teacher's classes when modal opens and no classId is pre-set
    useEffect(() => {
        if (!isOpen || !needsClassSelector || !currentUser) return;
        const load = async () => {
            setLoadingClasses(true);
            try {
                const q = query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid));
                const snap = await getDocs(q);
                const opts: ClassOption[] = snap.docs.map(d => ({ id: d.id, name: (d.data() as any).name }));
                setClasses(opts);
                if (opts.length === 1) {
                    setSelectedClassId(opts[0].id);
                    setSelectedClassName(opts[0].name);
                }
            } catch (err) {
                console.error('Error loading classes:', err);
            } finally {
                setLoadingClasses(false);
            }
        };
        load();
    }, [isOpen, needsClassSelector, currentUser]);
 
    if (!isOpen) return null;
 
    const resolvedClassId   = needsClassSelector ? selectedClassId   : propClassId;
    const resolvedClassName = needsClassSelector ? selectedClassName : propClassName;
 
    const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        setSelectedClassId(id);
        const found = classes.find(c => c.id === id);
        setSelectedClassName(found?.name || '');
    };
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
 
        if (!title.trim())        { setError('Please enter a title'); return; }
        if (!description.trim())  { setError('Please enter a description'); return; }
        if (!dueDate)             { setError('Please select a due date'); return; }
        if (needsClassSelector && !resolvedClassId) {
            setError('Please select a class'); return;
        }
 
        if (attachmentFile) {
            if (attachmentFile.size > 5 * 1024 * 1024) { setError('File must be smaller than 5MB'); return; }
            const valid = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
            if (!valid.includes(attachmentFile.type)) { setError('File must be PDF or image (JPG, PNG, GIF)'); return; }
        }
 
        setSaving(true);
        let attachmentUrl = '';
 
        if (attachmentFile) {
            try {
                const fileRef = ref(storage, `assignments/${resolvedClassId}/${Date.now()}_${attachmentFile.name}`);
                const snap = await uploadBytes(fileRef, attachmentFile);
                attachmentUrl = await getDownloadURL(snap.ref);
            } catch {
                setError('Failed to upload file. Please try again.');
                setSaving(false);
                return;
            }
        }
 
        try {
            const dueDateTimestamp = Timestamp.fromDate(new Date(`${dueDate}T${dueTime}`));
            const data: any = {
                classId:      resolvedClassId,
                className:    resolvedClassName,
                teacherId:    currentUser!.uid,
                teacherName:  currentUser!.displayName || 'Teacher',
                title:        title.trim(),
                description:  description.trim(),
                dueDate:      dueDateTimestamp,
                createdAt:    Timestamp.now(),
                updatedAt:    Timestamp.now(),
                status:       'active',
                submissionCount: 0,
            };
            if (prompt.trim())       data.prompt       = prompt.trim();
            if (instructions.trim()) data.instructions = instructions.trim();
            if (minWords)            data.minWords     = minWords;
            if (maxWords)            data.maxWords     = maxWords;
            if (attachmentUrl)       data.attachmentUrl  = attachmentUrl;
            if (attachmentFile)      data.attachmentName = attachmentFile.name;
 
            await addDoc(collection(db, 'assignments'), data);
 
            setNotification({
                show: true,
                type: 'assignment-created',
                assignmentTitle: title.trim(),
                className: resolvedClassName,
                customMessage: 'Students will see it on their dashboard!',
            });
 
            // Reset
            setTitle(''); setDescription(''); setPrompt(''); setInstructions('');
            setMinWords(''); setMaxWords(''); setDueDate(''); setDueTime('23:59');
            setAttachmentFile(null); setSelectedClassId(''); setSelectedClassName('');
 
            if (onAssignmentCreated) onAssignmentCreated();
 
        } catch (err) {
            console.error('Error creating assignment:', err);
            setError('Failed to create assignment. Please try again.');
        } finally {
            setSaving(false);
        }
    };
 
    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}
            >
                {/* Modal */}
                <div
                    onClick={e => e.stopPropagation()}
                    style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '620px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                    {/* Header */}
                    <div style={{ marginBottom: '28px' }}>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 6px 0' }}>
                            📝 Create Assignment
                        </h2>
                        {!needsClassSelector && (
                            <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: 0 }}>
                                For: <strong style={{ color: C.primary }}>{propClassName}</strong>
                            </p>
                        )}
                    </div>
 
                    {/* Error */}
                    {error && (
                        <div style={{ background: '#fef2f2', border: `1.5px solid ${C.error}30`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: C.error, fontSize: '0.875rem', fontWeight: '600' }}>
                            {error}
                        </div>
                    )}
 
                    <form onSubmit={handleSubmit}>
 
                        {/* ── Class selector (only when no classId pre-set) ───── */}
                        {needsClassSelector && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={labelStyle}>Class *</label>
                                {loadingClasses ? (
                                    <div style={{ ...inputStyle, color: C.onSurfaceVariant, display: 'flex', alignItems: 'center' }}>Loading classes...</div>
                                ) : classes.length === 0 ? (
                                    <div style={{ background: C.surfaceLow, borderRadius: '10px', padding: '14px 16px', fontSize: '0.875rem', color: C.onSurfaceVariant }}>
                                        No classes found. Create a class first.
                                    </div>
                                ) : (
                                    <select
                                        value={selectedClassId}
                                        onChange={handleClassChange}
                                        required
                                        style={{ ...inputStyle, background: C.surfaceLowest, cursor: 'pointer' }}
                                        onFocus={e  => { e.target.style.borderColor = C.primary; }}
                                        onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                                    >
                                        <option value="">Select a class...</option>
                                        {classes.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}
 
                        {/* ── Title ────────────────────────────────────────────── */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Assignment Title *</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                                placeholder="e.g., Write a Personal Narrative" maxLength={100}
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.primary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }} />
                        </div>
 
                        {/* ── Description ──────────────────────────────────────── */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Description *</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="What should students write about?" rows={3}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={e => { (e.target as HTMLTextAreaElement).style.borderColor = C.primary; }}
                                onBlur={e  => { (e.target as HTMLTextAreaElement).style.borderColor = C.outlineVariant; }} />
                        </div>
 
                        {/* ── Writing prompt ────────────────────────────────────── */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Writing Prompt <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                                placeholder="Optional story starter or prompt pre-filled in the editor" rows={2}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={e => { (e.target as HTMLTextAreaElement).style.borderColor = C.primary; }}
                                onBlur={e  => { (e.target as HTMLTextAreaElement).style.borderColor = C.outlineVariant; }} />
                        </div>
 
                        {/* ── Instructions ─────────────────────────────────────── */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={labelStyle}>Additional Instructions <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <textarea value={instructions} onChange={e => setInstructions(e.target.value)}
                                placeholder="Any specific requirements or guidelines" rows={2}
                                style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                onFocus={e => { (e.target as HTMLTextAreaElement).style.borderColor = C.primary; }}
                                onBlur={e  => { (e.target as HTMLTextAreaElement).style.borderColor = C.outlineVariant; }} />
                        </div>
 
                        {/* ── Word count ────────────────────────────────────────── */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                            <div>
                                <label style={labelStyle}>Min Words <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <input type="number" value={minWords} min={0} placeholder="e.g., 200"
                                    onChange={e => setMinWords(e.target.value ? parseInt(e.target.value) : '')}
                                    style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }}
                                    onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }} />
                            </div>
                            <div>
                                <label style={labelStyle}>Max Words <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                                <input type="number" value={maxWords} min={0} placeholder="e.g., 1000"
                                    onChange={e => setMaxWords(e.target.value ? parseInt(e.target.value) : '')}
                                    style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }}
                                    onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }} />
                            </div>
                        </div>
 
                        {/* ── Due date + time ───────────────────────────────────── */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '18px' }}>
                            <div>
                                <label style={labelStyle}>Due Date *</label>
                                <input type="date" value={dueDate}
                                    onChange={e => setDueDate(e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }}
                                    onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }} />
                            </div>
                            <div>
                                <label style={labelStyle}>Time</label>
                                <input type="time" value={dueTime}
                                    onChange={e => setDueTime(e.target.value)}
                                    style={inputStyle}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }}
                                    onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }} />
                            </div>
                        </div>
 
                        {/* ── Attachment ────────────────────────────────────────── */}
                        <div style={{ marginBottom: '28px' }}>
                            <label style={labelStyle}>Attachment <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                            <div style={{ border: `1.5px dashed ${attachmentFile ? C.primary : C.outlineVariant}`, borderRadius: '10px', padding: '16px', background: attachmentFile ? C.primaryContainer : C.surface, transition: 'all 0.15s' }}>
                                <input type="file" accept=".pdf,image/*"
                                    onChange={e => setAttachmentFile(e.target.files?.[0] || null)}
                                    style={{ width: '100%', fontSize: '0.85rem', fontFamily: "'Plus Jakarta Sans', sans-serif", cursor: 'pointer' }} />
                                {attachmentFile ? (
                                    <div style={{ marginTop: '8px', fontSize: '0.78rem', color: C.primary, fontWeight: '600' }}>
                                        📎 {attachmentFile.name} ({(attachmentFile.size / 1024).toFixed(0)} KB)
                                    </div>
                                ) : (
                                    <div style={{ marginTop: '6px', fontSize: '0.75rem', color: C.onSurfaceVariant }}>
                                        PDF or image · max 5 MB
                                    </div>
                                )}
                            </div>
                        </div>
 
                        {/* ── Buttons ───────────────────────────────────────────── */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={onClose} disabled={saving}
                                style={{ padding: '11px 24px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', background: C.surfaceLowest, color: C.onSurfaceVariant, fontSize: '0.875rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = C.surfaceLow; e.currentTarget.style.borderColor = C.onSurfaceVariant; } }}
                                onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; e.currentTarget.style.borderColor = C.outlineVariant; }}>
                                Cancel
                            </button>
                            <button type="submit" disabled={saving || (needsClassSelector && !selectedClassId)}
                                style={{ padding: '11px 28px', border: 'none', borderRadius: '9999px', background: (saving || (needsClassSelector && !selectedClassId)) ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', fontSize: '0.875rem', fontWeight: '700', cursor: (saving || (needsClassSelector && !selectedClassId)) ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: saving ? 'none' : `0 4px 14px ${C.primary}40`, transition: 'opacity 0.15s' }}
                                onMouseEnter={e => { if (!saving) e.currentTarget.style.opacity = '0.9'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                {saving ? 'Creating...' : 'Create Assignment'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
 
            {/* Notification */}
            <Notification
                isOpen={notification.show}
                onClose={() => { setNotification({ ...notification, show: false }); onClose(); }}
                type={notification.type}
                assignmentTitle={notification.assignmentTitle}
                className={notification.className}
                customMessage={notification.customMessage}
                errorMessage={notification.errorMessage}
            />
        </>
    );
};
 
export default CreateAssignmentModal;